// PostHog product analytics. Browser only: the prerender (entry-server.tsx)
// never imports this. With no VITE_POSTHOG_KEY at build time (local dev,
// branch previews) nothing loads and nothing is sent. posthog-js is about a
// sixth of the site's JavaScript, so it is its own chunk, fetched after the
// page has hydrated instead of before it.
import { APP_URL } from './appUrls';

const KEY = import.meta.env.VITE_POSTHOG_KEY as string | undefined;
// VITE_POSTHOG_HOST is the reverse proxy (https://e.statio.dev, PostHog's managed
// proxy, CNAME in Cloudflare), so ad blockers that block posthog.com still let
// events through. Without it, events go straight to PostHog US.
const HOST = (import.meta.env.VITE_POSTHOG_HOST as string | undefined) || 'https://us.i.posthog.com';

/** Which outbound clicks count as conversions, named by where they lead. */
function ctaFor(href: string): string | null {
  if (href.startsWith(`${APP_URL}/signup`)) return 'signup';
  if (href.startsWith(`${APP_URL}/login`)) return 'login';
  if (href.includes('statio-downloads')) return 'download_file';
  if (href.endsWith('/download') || href.includes('/download#')) return 'download_page';
  return null;
}

export async function initAnalytics(): Promise<void> {
  if (!KEY || typeof window === 'undefined') return;
  const { default: posthog } = await import('posthog-js');
  posthog.init(KEY, {
    api_host: HOST,
    ui_host: 'https://us.posthog.com', // toolbar and app links still open PostHog itself
    defaults: '2025-05-24',
    capture_pageview: 'history_change', // React Router navigations count as page views
    person_profiles: 'identified_only', // anonymous visitors stay anonymous
  });

  // One delegated listener instead of an onClick on every button.
  document.addEventListener('click', (e) => {
    const a = (e.target as Element | null)?.closest?.('a[href]') as HTMLAnchorElement | null;
    if (!a) return;
    const cta = ctaFor(a.href);
    if (!cta) return;
    posthog.capture('cta_click', {
      cta,
      href: a.href,
      text: a.textContent?.trim().slice(0, 80),
      plan: new URL(a.href).searchParams.get('plan') ?? undefined,
      path: window.location.pathname,
    });
  });
}
